import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Alert, Box, Button, Collapse, IconButton, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography, alpha, useTheme, Chip,
  FormControl, InputLabel, Select, MenuItem, Avatar, InputAdornment
} from '@mui/material';
import {
  KeyboardArrowDown, KeyboardArrowUp, Refresh, Search, FilterListOff,
  TrendingUp, FileDownload
} from '@mui/icons-material';
import { Users, MapPin, Briefcase, ArrowUpDown, RotateCcw } from 'lucide-react';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

import PageHeader from '../../components/headers/Header';
import { getAllEmployees, getEmployeeImage } from '../../services/HRstaff';
import api from '../../api';
import { API_URL } from '../../config';
import { getAllCareers } from '../../services/careerAPIs';
import { getOffices } from '../../services/officeAPIs';
import marabesLogo from '../../assets/images/logos/Marabes.png';

// Fallback position format function in case formatPositionsDisplay is complex
const formatPositionsDisplay = (employee) => {
  if (employee?.positions && Array.isArray(employee.positions)) {
    return employee.positions
      .filter(pos => pos.fte != null && pos.fte >= 0.5)
      .map(pos => pos.positionName?.trim() || '')
      .filter(name => name !== '')
      .join('; ');
  }
  return employee?.position || 'N/A';
};

const formatDate = (dateString) => {
  if (!dateString) return 'Current';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return format(date, 'dd/MM/yyyy');
  } catch {
    return 'N/A';
  }
};

const getBase64FromUrl = async (url) => {
  try {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.warn("Could not load image for PDF:", url);
    return null;
  }
};

// Row Component for each Employee
const EmployeeRow = React.memo(function EmployeeRow({ employee }) {
  const [open, setOpen] = useState(false);
  const theme = useTheme();

  return (
    <React.Fragment>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }} hover>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            disabled={!employee.careers || employee.careers.length === 0}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar src={employee.imageUrl} alt={employee.name} sx={{ width: 40, height: 40 }}>
              {employee.name ? employee.name.charAt(0) : '?'}
            </Avatar>
            <Typography variant="body2" fontWeight={700}>
              {employee.name}
            </Typography>
          </Stack>
        </TableCell>
        <TableCell>{employee.department}</TableCell>
        <TableCell>{employee.office}</TableCell>
        <TableCell>{employee.position}</TableCell>
        <TableCell>{formatDate(employee.startDate)}</TableCell>
        <TableCell align="center">
          <Chip
            label={`${employee.careers ? employee.careers.length : 0} Record(s)`}
            size="small"
            color={employee.careers && employee.careers.length > 0 ? "primary" : "default"}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.02), borderRadius: 2 }}>
              <Typography variant="subtitle2" gutterBottom component="div" fontWeight={700}>
                Career History
              </Typography>
              <Table size="small" aria-label="careers">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Position Title</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Projects</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Period</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employee.careers && employee.careers.map((careerRow) => (
                    <TableRow key={careerRow.id}>
                      <TableCell component="th" scope="row">
                        {careerRow.positionTitle}
                      </TableCell>
                      <TableCell>
                         <Box display="flex" gap={0.5} flexWrap="wrap">
                          {careerRow.projectNames
                            ? careerRow.projectNames.split(',').map(p => p.trim()).filter(Boolean).map(p => (
                              <Chip key={p} label={p} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                            ))
                            : <Typography variant="caption" color="text.secondary">None</Typography>
                          }
                        </Box>
                      </TableCell>
                      <TableCell>
                        {formatDate(careerRow.startDate)} – {formatDate(careerRow.endDate)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
});

export default function EmployeesCareer() {
  const theme = useTheme();
  const tableRef = useRef(null);

  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [offices, setOffices] = useState([]);
  const [allPositions, setAllPositions] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState('');
  
  // Filters
  const [searchName, setSearchName] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterOffice, setFilterOffice] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [empRes, careersRes, deptRes, officeRes] = await Promise.all([
        getAllEmployees(),
        getAllCareers(),
        api.get(`${API_URL}/departments`),
        getOffices()
      ]);

      const rawEmps = Array.isArray(empRes?.data) ? empRes.data : Array.isArray(empRes) ? empRes : empRes?.users || [];
      const depts = deptRes.data || [];
      const offs = officeRes || [];
      
      setDepartments(depts);
      setOffices(offs);

      // Process positions from employees to populate filter
      const posSet = new Set();

      const empsProcessed = rawEmps
        .filter(e => (e.departmentName || e.department?.name || '').toLowerCase() !== 'facility')
        .map(e => {
        const name = e.name || e.fullName || `${e.firstName || ''} ${e.lastName || ''}`.trim();
        const deptName = e.departmentName || e.department?.name || 'N/A';
        const officeName = e.officeName || e.office?.name || 'N/A';
        const positionStr = formatPositionsDisplay(e);
        
        if (positionStr && positionStr !== 'N/A') {
            positionStr.split(';').forEach(p => posSet.add(p.trim()));
        }

        return {
          id: e.employeeId || e.id,
          name,
          department: deptName,
          office: officeName,
          position: positionStr,
          startDate: e.dateOfJoining || e.startDate,
          imageUrl: e.image ? `${API_URL}/employees/image/${e.employeeId || e.id}` : null,
          careers: careersRes.filter(c => String(c.employeeId) === String(e.employeeId || e.id))
        };
      });

      setAllPositions(Array.from(posSet).sort());
      setEmployees(empsProcessed);

    } catch (err) {
      console.error('Failure loading data:', err);
      setError('Failed to load career data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const hasActiveFilters = searchName || filterDepartment || filterOffice || filterPosition;

  const clearAllFilters = () => {
    setSearchName('');
    setFilterDepartment('');
    setFilterOffice('');
    setFilterPosition('');
  };

  const filteredEmployees = useMemo(() => {
    let result = employees.filter((emp) => {
      if (searchName && !emp.name.toLowerCase().includes(searchName.toLowerCase())) return false;
      if (filterDepartment && emp.department !== filterDepartment) return false;
      if (filterOffice && emp.office !== filterOffice) return false;
      if (filterPosition && !emp.position.includes(filterPosition)) return false;
      return true;
    });

    result.sort((a, b) => {
      let valA = a[sortBy] || '';
      let valB = b[sortBy] || '';

      if (sortBy === 'startDate') {
        valA = new Date(valA).getTime() || 0;
        valB = new Date(valB).getTime() || 0;
      } else {
        valA = valA.toString().toLowerCase();
        valB = valB.toString().toLowerCase();
      }

      if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
      if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [employees, searchName, filterDepartment, filterOffice, filterPosition, sortBy, sortOrder]);

  const handleExportPDF = async () => {
    try {
      setIsExporting(true);
      const doc = new jsPDF('p', 'mm', 'a4');
      const today = new Date().toLocaleDateString('en-GB');
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 10;

      // Load axo base64
      const axoLogoUrl = '/cLogo.png';
      const axoBase64 = await getBase64FromUrl(axoLogoUrl);

      const tableColumn = ["Profile", "Employee Name", "Department", "Office", "Current Position", "Start Date", "Records"];
      const columnStyles = {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 40 },
        2: { cellWidth: 26 },
        3: { cellWidth: 24 },
        4: { cellWidth: 40 },
        5: { cellWidth: 26 },
        6: { cellWidth: 22 },
      };

      const drawPageHeader = (isFirstPage) => {
        if (isFirstPage) {
          // Draw logos
          try {
            doc.addImage(marabesLogo, 'PNG', margin, 10, 35, 12);
          } catch (e) {
            console.warn("Marabes logo load failed", e);
          }
          if (axoBase64) {
            doc.addImage(axoBase64, 'PNG', pageWidth - margin - 35, 10, 35, 12);
          }

          // Draw header
          doc.setFontSize(16);
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(40, 58, 139);
          doc.text('Employees Career History Report', pageWidth / 2, 25, { align: 'center' });
          
          doc.setFontSize(10);
          doc.setTextColor(100, 100, 100);
          doc.text(`Date: ${today}`, margin, 35);
        }

        // Draw Table Header
        autoTable(doc, {
            startY: isFirstPage ? 38 : margin,
            head: [tableColumn],
            body: [],
            theme: 'grid',
            headStyles: { fillColor: [63, 81, 181] },
            styles: { fontSize: 8, cellPadding: 3, minCellHeight: 10 },
            columnStyles: columnStyles,
            margin: { left: margin, right: margin }
        });
      };

      // Pre-fetch base64 for avatars of filtered employees
      const base64Cache = {};
      for (const emp of filteredEmployees) {
        if (emp.imageUrl) {
          try {
            const res = await fetch(emp.imageUrl);
            const blob = await res.blob();
            base64Cache[emp.id] = await new Promise(resolve => {
              const reader = new FileReader();
              reader.onloadend = () => resolve(reader.result);
              reader.readAsDataURL(blob);
            });
          } catch (e) {
            console.warn("Could not fetch avatar for PDF", emp.id);
          }
        }
      }

      drawPageHeader(true);
      let currentY = doc.lastAutoTable.finalY;

      filteredEmployees.forEach(emp => {
        let estimatedHeight = 12;
        if (emp.careers && emp.careers.length > 0) {
            estimatedHeight += 16 + (emp.careers.length * 10);
        }

        // If the employee block doesn't fit on this page, force a new page
        if (estimatedHeight < 240 && currentY + estimatedHeight > 285) {
            doc.addPage();
            drawPageHeader(false);
            currentY = doc.lastAutoTable.finalY;
        }

        const tableRows = [];
        const mainStyle = { minCellHeight: 12, cellPadding: 3 };

        // Main Row
        tableRows.push([
          { content: "", styles: mainStyle }, // For image
          { content: emp.name, styles: mainStyle },
          { content: emp.department, styles: mainStyle },
          { content: emp.office, styles: mainStyle },
          { content: emp.position, styles: mainStyle },
          { content: formatDate(emp.startDate), styles: mainStyle },
          { content: `${emp.careers ? emp.careers.length : 0} Record(s)`, styles: mainStyle }
        ]);

        // Career Sub-rows
        if (emp.careers && emp.careers.length > 0) {
          const subBg = [248, 250, 252]; 

          // Sub-header Title
          tableRows.push([
            { content: "", colSpan: 1, styles: { fillColor: [255, 255, 255] } }, 
            { content: "Career History", colSpan: 6, styles: { fillColor: subBg, fontStyle: 'bold', textColor: [40, 58, 139] } }
          ]);

          // Sub-table Headers
          tableRows.push([
            { content: "", colSpan: 1, styles: { fillColor: [255, 255, 255] } },
            { content: "", colSpan: 1, styles: { fillColor: subBg } }, 
            { content: "Position Title", colSpan: 2, styles: { fontStyle: 'bold', fillColor: subBg, textColor: [100, 100, 100] } },
            { content: "Projects", colSpan: 1, styles: { fontStyle: 'bold', fillColor: subBg, textColor: [100, 100, 100] } },
            { content: "Period", colSpan: 2, styles: { fontStyle: 'bold', fillColor: subBg, textColor: [100, 100, 100] } }
          ]);

          // Sub-table Data Rows
          emp.careers.forEach((career, idx) => {
            const isLast = idx === emp.careers.length - 1;
            tableRows.push([
              { content: "", colSpan: 1, styles: { fillColor: [255, 255, 255] } },
              { content: "", colSpan: 1, styles: { fillColor: subBg } },
              { content: career.positionTitle || 'N/A', colSpan: 2, styles: { fillColor: subBg, cellPadding: { bottom: isLast ? 4 : 2, top: 2, left: 2, right: 2 } } },
              { content: career.projectNames || 'None', colSpan: 1, styles: { fillColor: subBg, cellPadding: { bottom: isLast ? 4 : 2, top: 2, left: 2, right: 2 } } },
              { content: `${formatDate(career.startDate)} - ${formatDate(career.endDate)}`, colSpan: 2, styles: { fillColor: subBg, cellPadding: { bottom: isLast ? 4 : 2, top: 2, left: 2, right: 2 } } }
            ]);
          });
        }

        autoTable(doc, {
          startY: currentY,
          body: tableRows,
          theme: 'grid',
          showHead: false,
          styles: { fontSize: 8, cellPadding: 2, minCellHeight: 6 },
          margin: { left: margin, right: margin },
          columnStyles: columnStyles,
          didDrawCell: (data) => {
            // Draw bold external borders for the main row
            if (data.section === 'body' && data.row.index === 0) {
              doc.setDrawColor(40, 58, 139); // Dark blue
              doc.setLineWidth(0.6); // Bold line
              
              // Top border
              doc.line(data.cell.x, data.cell.y, data.cell.x + data.cell.width, data.cell.y);
              // Bottom border
              doc.line(data.cell.x, data.cell.y + data.cell.height, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
              // Left border (first cell)
              if (data.column.index === 0) {
                doc.line(data.cell.x, data.cell.y, data.cell.x, data.cell.y + data.cell.height);
              }
              // Right border (last cell)
              if (data.column.index === 6) {
                doc.line(data.cell.x + data.cell.width, data.cell.y, data.cell.x + data.cell.width, data.cell.y + data.cell.height);
              }
            }

            // Draw image only on the first cell of the main row for this employee
            if (data.section === 'body' && data.row.index === 0 && data.column.index === 0) {
              const base64Img = base64Cache[emp.id];
              if (base64Img) {
                const dim = 10;
                const x = data.cell.x + (data.cell.width - dim) / 2;
                const y = data.cell.y + (data.cell.height - dim) / 2;
                
                let format = 'PNG';
                if (base64Img.toLowerCase().includes('image/jpeg') || base64Img.toLowerCase().includes('image/jpg')) format = 'JPEG';
                
                try {
                  doc.addImage(base64Img, format, x, y, dim, dim);
                } catch(e) {
                  console.warn("Failed to add avatar image to PDF", e);
                }
              }
            }
          }
        });
        
        currentY = doc.lastAutoTable.finalY;
      });

      doc.save(`Employees_Career_History_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Error generating PDF: ' + (error.message || error.toString()));
    } finally {
      setIsExporting(false);
    }
  };

  const headerActions = (
    <Stack direction="row" spacing={1}>
      <Button
        variant="outlined"
        size="small"
        startIcon={<Refresh />}
        onClick={loadData}
        disabled={loading}
      >
        Refresh
      </Button>
      <Button
        variant="contained"
        size="small"
        color="secondary"
        startIcon={<FileDownload />}
        onClick={handleExportPDF}
        disabled={loading || isExporting || filteredEmployees.length === 0}
      >
        {isExporting ? 'Exporting...' : 'Export PDF'}
      </Button>
    </Stack>
  );

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 3, minHeight: '100vh', bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
      <PageHeader
        title="Employees Career"
        subtitle="Overview of all employees and their detailed career history."
        actions={headerActions}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* FILTER BAR */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          mb: 2,
          p: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems="center" flexWrap="wrap">
          {/* Search by name */}
          <TextField
            id="filter-search-name"
            size="small"
            placeholder="Search by name…"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            sx={{ minWidth: 200, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />

          {/* Department filter */}
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id="filter-department-label">Department</InputLabel>
            <Select
              labelId="filter-department-label"
              label="Department"
              value={filterDepartment}
              startAdornment={
                <InputAdornment position="start">
                  <Users size={16} style={{ color: '#1976d2', marginRight: -4 }} />
                </InputAdornment>
              }
              onChange={(e) => setFilterDepartment(e.target.value)}
            >
              <MenuItem value="">All Departments</MenuItem>
              {departments.map(d => (
                <MenuItem key={d.departmentId || d.id} value={d.name || d.departmentName}>{d.name || d.departmentName}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Office filter */}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel id="filter-office-label">Office</InputLabel>
            <Select
              labelId="filter-office-label"
              label="Office"
              value={filterOffice}
              startAdornment={
                <InputAdornment position="start">
                  <MapPin size={16} style={{ color: '#1976d2', marginRight: -4 }} />
                </InputAdornment>
              }
              onChange={(e) => setFilterOffice(e.target.value)}
            >
              <MenuItem value="">All Offices</MenuItem>
              {offices.map(o => (
                <MenuItem key={o.id} value={o.name}>{o.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Position filter */}
          <FormControl size="small" sx={{ minWidth: 165 }}>
            <InputLabel id="filter-position-label">Position</InputLabel>
            <Select
              labelId="filter-position-label"
              label="Position"
              value={filterPosition}
              startAdornment={
                <InputAdornment position="start">
                  <Briefcase size={16} style={{ color: '#1976d2', marginRight: -4 }} />
                </InputAdornment>
              }
              onChange={(e) => setFilterPosition(e.target.value)}
            >
              <MenuItem value="">All Positions</MenuItem>
              {allPositions.map(p => (
                <MenuItem key={p} value={p}>{p}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sort By filter */}
          <FormControl size="small" sx={{ minWidth: 155 }}>
            <InputLabel id="sort-by-label">Sort By</InputLabel>
            <Select
              labelId="sort-by-label"
              label="Sort By"
              value={sortBy}
              startAdornment={
                <InputAdornment position="start">
                  <ArrowUpDown size={16} style={{ color: '#1976d2', marginRight: -4 }} />
                </InputAdornment>
              }
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="startDate">Start Date</MenuItem>
              <MenuItem value="office">Office</MenuItem>
            </Select>
          </FormControl>
          
          <Tooltip title={sortOrder === 'asc' ? "Ascending" : "Descending"}>
            <IconButton onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}>
               {sortOrder === 'asc' ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </Tooltip>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Tooltip title="Clear all filters">
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<RotateCcw size={16} />}
                onClick={clearAllFilters}
                sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap', borderRadius: 2 }}
              >
                Reset Filters
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          bgcolor: theme.palette.background.paper,
        }}
      >
        <Box sx={{ px: 2.5, py: 2, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <TrendingUp color="primary" />
              <Box>
                <Typography variant="subtitle1" fontWeight={800}>
                  Employees Career List
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filteredEmployees.length} of {employees.length} employee{employees.length === 1 ? '' : 's'}
                  {hasActiveFilters ? ' (filtered)' : ''}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography color="text.secondary">Loading career histories...</Typography>
          </Box>
        ) : filteredEmployees.length === 0 ? (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
              {hasActiveFilters ? 'No matching records' : 'No employees logged'}
            </Typography>
          </Box>
        ) : (
          <div ref={tableRef} style={{ backgroundColor: '#ffffff', padding: '8px' }}>
            <TableContainer>
              <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell sx={{ fontWeight: 800 }}>Employee Name</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Department</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Office</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Current Position</TableCell>
                  <TableCell sx={{ fontWeight: 800 }}>Start Date</TableCell>
                  <TableCell sx={{ fontWeight: 800 }} align="center">Career Records</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredEmployees.map((emp) => (
                  <EmployeeRow key={emp.id} employee={emp} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </div>
        )}
      </Paper>
    </Box>
  );
}
